import { appQueryClient } from "app/AppQueryClient";
import {
  contextFeatureFlagRetrieve,
  getContextFeatureFlagRetrieveQueryKey,
  useContextFeatureFlagRetrieveSuspense,
} from "gen/endpoints/feature-flags/feature-flags";
import { FeatureFlagNameEnum } from "gen/model/featureFlagNameEnum";
import { get } from "lodash-es";

// We want to cache feature flags but have caching turned off by default.
// We set them specifically for this request.
// https://tanstack.com/query/v4/docs/react/guides/caching
const STALE_TIME = 1 * 60 * 1000; // 1 minute
const CACHE_TIME = 5 * 60 * 1000; // 5 minutes

export const useFeatureFlagActive = (
  flagName: FeatureFlagNameEnum,
): boolean => {
  const { data } = useContextFeatureFlagRetrieveSuspense({
    query: {
      staleTime: STALE_TIME,
      gcTime: CACHE_TIME,
    },
  });
  return get(data.feature_flags, flagName, false);
};

export const useFeatureFlagsActive = (
  flagNames: FeatureFlagNameEnum[],
): Map<FeatureFlagNameEnum, boolean> => {
  const result = new Map<FeatureFlagNameEnum, boolean>();
  const { data } = useContextFeatureFlagRetrieveSuspense({
    query: {
      staleTime: STALE_TIME,
      gcTime: CACHE_TIME,
    },
  });

  flagNames.forEach((flagName) => {
    result.set(flagName, get(data.feature_flags, flagName, false));
  });

  return result;
};

export const isFeatureFlagActive = async (
  flagName: FeatureFlagNameEnum,
): Promise<boolean> => {
  const data = await appQueryClient.ensureQueryData({
    queryKey: getContextFeatureFlagRetrieveQueryKey(),
    queryFn: () => contextFeatureFlagRetrieve(),
    staleTime: STALE_TIME,
    gcTime: CACHE_TIME,
  });
  return get(data.feature_flags, flagName, false);
};

export const getFeatureFlagValues = async (
  flagNames: FeatureFlagNameEnum[],
): Promise<Map<FeatureFlagNameEnum, boolean>> => {
  const result = new Map<FeatureFlagNameEnum, boolean>();
  const data = await appQueryClient.ensureQueryData({
    queryKey: getContextFeatureFlagRetrieveQueryKey(),
    queryFn: () => contextFeatureFlagRetrieve(),
    staleTime: STALE_TIME,
    gcTime: CACHE_TIME,
  });

  flagNames.forEach((flagName) => {
    result.set(flagName, get(data.feature_flags, flagName, false));
  });

  return result;
};
